import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import store, { RootState } from '../store';
import { useGetBooksMutation, useGetClassSubjectsQuery } from '../api';
import { setBooksData } from '../features/books/booksSlice';
import { setJasperBooksData } from '../features/jasperBooks/jasperBooksSlice';
import { setIsJasperLoading } from '../features/jasper/jasperSlice';
import { setSubjectsData } from '../features/classSubjects/classSubjectsSlice';

interface ClassOption {
    value: string;
    label: string;
}

interface UseBookLibraryFetchingReturn {
    // Class subjects data
    classSubjectsData: any;
    isClassSubjectsLoading: boolean;
    classSubjectsError: any;
    refetchClassSubjects: () => void;

    // Books data
    booksData: any;
    isBooksLoading: boolean;
    booksError: any;

    // Processed class options
    classOptions: ClassOption[];

    // Fetch function
    fetchBooks: (classIds: string | string[]) => void;

    // Combined error state
    hasError: boolean;
}

export const useBookLibraryFetching = (
    source: string
): UseBookLibraryFetchingReturn => {
    const dispatch = useDispatch();

    // Priority order for sorting class options
    const priorityOrder = [
        'All Books',
        'Play Group',
        'Nursery',
        'LKG',
        'JKG',
        'UKG',
        'SKG',
    ];

    // State for processed class options
    const [classOptions, setClassOptions] = useState<ClassOption[]>([]);

    // Selectors
    const boardId = useSelector(
        (state: RootState) => state.SchoolInfo.data.boardId
    );
    const academicyearid = useSelector(
        (state: RootState) => state.SchoolInfo.data.academicyearid
    );
    const product = useSelector((state: RootState) => state.product);
    const productType = useSelector((state: RootState) => state.product) as
        | 'Buildr'
        | 'ActiveTeach';

    // API hooks
    const {
        data: class_subjects_response,
        error: classSubjectsError,
        isLoading: isClassSubjectsLoading,
        refetch: refetchClassSubjects,
    } = useGetClassSubjectsQuery({
        school_id: store.getState().SchoolInfo.data.school.id,
        academic_year_id: academicyearid,
        board_id: boardId,
        product: product,
    });
    const [
        getBooks,
        { isLoading: isBooksLoading, data: booksData, error: booksError },
    ] = useGetBooksMutation();

    // Initial fetch on mount
    useEffect(() => {
        refetchClassSubjects();
    }, [refetchClassSubjects]);

    // Process class subjects data into options
    useEffect(() => {
        if (class_subjects_response?.success) {
            dispatch(setSubjectsData(class_subjects_response?.data))
            const tempClassOptions =
                class_subjects_response.data.classSubjects.map(
                    (cls: { classId: any; className: any }) => ({
                        value: cls?.classId,
                        label: cls?.className,
                    })
                );

            tempClassOptions.push({ value: 'all', label: 'All Books' });

            // Sort based on priority order
            tempClassOptions.sort((a: any, b: any) => {
                const aIndex = priorityOrder.indexOf(a.label);
                const bIndex = priorityOrder.indexOf(b.label);

                //  Sort by priority order if both in list
                if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;

                //  If only one is in priority list, it comes first
                if (aIndex !== -1) return -1;
                if (bIndex !== -1) return 1;

                //  Extract number from label
                const aMatch = a.label.match(/Class(\d+)/);
                const bMatch = b.label.match(/Class(\d+)/);

                if (aMatch && bMatch) {
                    const aNum = parseInt(aMatch[1], 10);
                    const bNum = parseInt(bMatch[1], 10);
                    return aNum - bNum;
                }

                // Fallback to alphabetical sort
                return a.label.localeCompare(b.label);
            });
            setClassOptions(tempClassOptions);
        }
    }, [class_subjects_response]);

    // Fetch books function
    const fetchBooks = useCallback(
        (classIds: string | string[]) => {
            if (!class_subjects_response) return;

            let requestBody = {
                academic_year_id: 0,
                board_id: 0,
                class_subjects: class_subjects_response?.data?.classSubjects,
                platform: '',
            };

            const isAllBooks =
                typeof classIds === 'string' && classIds === 'all';

            if (!isAllBooks) {
                const classIdsArray = Array.isArray(classIds)
                    ? classIds
                    : [classIds];
                const filteredClassIds = classIdsArray.filter(
                    (classId) => classId !== 'all'
                );

                const classSubjects = filteredClassIds.map((classId) => {
                    const selectedCls =
                        class_subjects_response?.data?.classSubjects?.find(
                            (cls: any) => cls?.classId.toString() === classId
                        );
                    return {
                        class_id: parseInt(classId),
                        subject_ids: selectedCls?.subjects?.map(
                            (subj: any) => subj?.subjectId
                        ),
                        book_isbns: selectedCls?.subjects?.map(
                            (subj: any) => subj?.bookISBN ?? null
                        ),
                        term_count: selectedCls?.termCount,
                    };
                });

                requestBody = {
                    academic_year_id: academicyearid,
                    board_id: boardId,
                    class_subjects: classSubjects,
                    platform: productType,
                };
            }

            getBooks(requestBody)
                .unwrap()
                .then((result) => {
                    if (source == 'jasper') {
                        dispatch(setJasperBooksData(result.data));
                        dispatch(setIsJasperLoading(false));
                    } else dispatch(setBooksData(result.data));
                })
                .catch((error) => {
                    console.error('Error fetching books:', error);
                });
        },
        [
            class_subjects_response,
            academicyearid,
            boardId,
            productType,
            getBooks,
            dispatch,
        ]
    );

    return {
        // Class subjects
        classSubjectsData: class_subjects_response,
        isClassSubjectsLoading,
        classSubjectsError,
        refetchClassSubjects,

        // Books
        booksData,
        isBooksLoading,
        booksError,

        // Processed data
        classOptions,

        // Functions
        fetchBooks,

        // Combined states
        hasError: !!classSubjectsError || !!booksError,
    };
};
